import React, {useEffect, useState} from 'react';
import './AddClientModal.css';
import {FiCreditCard} from 'react-icons/fi';
import {BsCameraFill, BsCardImage, BsChatFill, BsExclamationTriangleFill, BsPencilSquare, BsX} from 'react-icons/bs';
import {FaCreditCard, FaMoneyBillWave} from 'react-icons/fa';
import DynamicModal from '../dynamicModal/DynamicModal';
import GalleryModal from '../galleryModal/GalleryModal';
import DrawingCanvas from '../drawingCanvas/DrawingCanvas';
import IdCardScanner from '../idCardScanner/IdCardScanner';
import ReviewModal from '../reviewModal/ReviewModal';
import axios from 'axios';
import {BASE_URL, USER_ENDPOINTS} from '../../Constants';
import AttachmentService from '../../services/AttachmentService';
import ContractModal from '../contractModal/ContractModal';

// Predefined furniture types list
const FURNITURE_TYPES = [
    'Bucătărie',
    'Dormitor',
    'Living',
    'Hol',
    'Dulapuri',
    'Dressing',
    'Baie',
    'Birou',
    'Bibliotecă',
    'Mobilă copii',
    'Mobilă la comandă',
    'Altele'
];

const CLIENT_TYPES = [
    'Persoană fizică',
    'Persoană juridică'
];

// Helper function to get payment method icon and color
const getPaymentIcon = (paymentMethod) => {
    switch (paymentMethod) {
        case 'Cash':
            return <FaMoneyBillWave style={{color: '#f1c40f'}}/>; // Yellow for cash
        case 'Card':
            return <FaCreditCard style={{color: '#27ae60'}}/>; // Green for card
        default:
            return <FiCreditCard/>;
    }
};

// Utility function to auto-calculate contract status based on business rules
const calculateContractStatus = (initialPayment, hasContract = false) => {
    const payment = parseFloat(initialPayment) || 0;

    // Rule 1: No initial payment and no contract -> In ofertare
    if (payment === 0 && !hasContract) {
        return 'In ofertare';
    }

    // Rule 2: Has initial payment but no contract -> Fara contract
    if (payment > 0 && !hasContract) {
        return 'Fara contract';
    }

    // Rule 3: Has initial payment and has contract -> Contract
    if (payment > 0 && hasContract) {
        return 'Contract';
    }

    // Default fallback
    return 'In ofertare';
};

// Helper function to get appropriate colors for contract status
const getContractStatusColor = (status) => {
    switch (status) {
        case 'Contract':
            return '#28a745'; // Green
        case 'In ofertare':
            return '#ffc107'; // Yellow
        case 'Fara contract':
            return '#dc3545'; // Red
        default:
            return '#ffc107';
    }
};

const ConfirmationModal = ({isOpen, onConfirm, onCancel, message}) => {
    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                position: 'relative'
            }}>
                <button
                    onClick={onCancel}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: '#e74c3c',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        color: 'white',
                        fontSize: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                    }}
                >
                    ×
                </button>

                <div style={{
                    fontSize: '16px',
                    color: '#2c3e50',
                    marginBottom: '32px',
                    lineHeight: '1.5',
                    fontWeight: '500',
                    paddingRight: '40px'
                }}>
                    {message}
                </div>

                <div style={{
                    display: 'flex',
                    gap: '16px'
                }}>
                    <button
                        onClick={onConfirm}
                        style={{
                            flex: 1,
                            backgroundColor: '#1abc9c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Da
                    </button>
                    <button
                        onClick={onCancel}
                        style={{
                            flex: 1,
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Nu
                    </button>
                </div>
            </div>
        </div>
    );
};

function AddClientModal({isOpen, onClose, onSave, initialData = null}) {
    // This component handles:
    // 1. Client info form
    // 2. Measurements and installations scheduling
    // 3. Attachments - both photos (attachType=photo) and drawings (attachType=project)
    // 4. Customer reviews
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        contractNumber: '02',
        clientType: 'Persoană fizică',
        contract: '',
        email: '',
        phone1: '',
        phone2: '',

        firstName: '',
        lastName: '',
        cnp: '',
        idSeries: '',
        furnitureType: '', // Will be converted to array for multiselect
        address: '',
        details: '',
        advance: '',
        advancePaymentMethod: 'Cash',
        rest: '',
        restPaymentMethod: 'Card',
        total: '',
        companyName: '',
        cui: '',
        companyAddress: ''
    });

    const [customerId, setCustomerId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showMeasurementsModal, setShowMeasurementsModal] = useState(false);
    const [showInstallationsModal, setShowInstallationsModal] = useState(false);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
    const [showIdScanner, setShowIdScanner] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [measurements, setMeasurements] = useState([]);
    const [installations, setInstallations] = useState([]);
    const [initialMeasurements, setInitialMeasurements] = useState([]);
    const [initialInstallations, setInitialInstallations] = useState([]);
    const [isRemainingPaymentPaid, setIsRemainingPaymentPaid] = useState(false);
    const [contractNumber, setContractNumber] = useState(null);
    const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);

    // Attachment tracking state
    const [selectedImages, setSelectedImages] = useState([]);
    const [initialImages, setInitialImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [deletedImageIds, setDeletedImageIds] = useState([]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [customerReview, setCustomerReview] = useState(null);
    const [isLoadingReview, setIsLoadingReview] = useState(false);
    const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [hasExistingContract, setHasExistingContract] = useState(false);
    const [isCheckingContract, setIsCheckingContract] = useState(false);

    // New state for multiselect furniture types
    const [selectedFurnitureTypes, setSelectedFurnitureTypes] = useState([]);
    const [showFurnitureDropdown, setShowFurnitureDropdown] = useState(false);
    const [showClientTypeDropdown, setShowClientTypeDropdown] = useState(false);

    // Auto-calculate contract status when advance payment or contract existence changes
    useEffect(() => {
        const calculatedStatus = calculateContractStatus(formData.advance, hasExistingContract);

        // Only update if the calculated status is different from current
        if (calculatedStatus !== formData.contract) {
            setFormData(prevState => ({
                ...prevState,
                contract: calculatedStatus
            }));
        }
    }, [formData.advance, hasExistingContract]);

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateRomanianPhone = (phone) => {
        // Remove all non-digit characters
        const cleanPhone = phone.replace(/\D/g, '');

        // Romanian mobile number patterns:
        // 07XXXXXXXX (10 digits starting with 07)
        // +4070XXXXXXXX (international format)
        // 4070XXXXXXXX (without +)

        if (cleanPhone.length === 10 && cleanPhone.startsWith('07')) {
            return true;
        }

        if (cleanPhone.length === 12 && cleanPhone.startsWith('4070')) {
            return true;
        }

        return false;
    };

    const hasExistingAdvance = () => {
        return isEditMode && initialData && (
            (initialData.advance && parseFloat(initialData.advance) > 0) ||
            (initialData.initialPayment && parseFloat(initialData.initialPayment) > 0)
        );
    };

// Helper function to determine if advance field should be disabled
    const isAdvanceDisabled = () => {
        return formData.contract === 'Contract' || hasExistingAdvance();
    };

// Helper function to determine if total field should be disabled
    const isTotalDisabled = () => {
        return formData.contract === 'Contract' || isRemainingPaymentPaid;
    };

    const validateCNP = (cnp) => {
        // Remove any non-digit characters
        const cleanCNP = cnp.replace(/\D/g, '');

        // Must be exactly 13 digits
        if (cleanCNP.length !== 13) {
            return false;
        }

        // First digit must be 1-6 (gender and century)
        const firstDigit = parseInt(cleanCNP[0]);
        if (firstDigit < 1 || firstDigit > 6) {
            return false;
        }

        // Check birth date (positions 1-6: YYMMDD)
        const year = parseInt(cleanCNP.substr(1, 2));
        const month = parseInt(cleanCNP.substr(3, 2));
        const day = parseInt(cleanCNP.substr(5, 2));

        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return false;
        }

        // Check control digit (last digit)
        const controlDigits = "279146358279";
        let sum = 0;

        for (let i = 0; i < 12; i++) {
            sum += parseInt(cleanCNP[i]) * parseInt(controlDigits[i]);
        }

        const remainder = sum % 11;
        const controlDigit = remainder < 10 ? remainder : 1;

        return parseInt(cleanCNP[12]) === controlDigit;
    };

    // Function to check if a date is in the past
    const isPastDate = (dateString) => {
        if (!dateString) return false;

        const today = new Date();
        const selectedDate = new Date(dateString);

        // Set both dates to start of day for comparison
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);

        return selectedDate < today;
    };

    const validateForm = () => {
        const errors = [];

        // Mandatory field validations
        // if (!formData.email.trim()) {
        //     errors.push('Email-ul este obligatoriu');
        // } else if (!validateEmail(formData.email)) {
        //     errors.push('Email-ul nu are un format valid');
        // }
        if (!validateEmail(formData.email) && formData.email.trim()) {
            errors.push('Email-ul nu are un format valid');
        }

        if (!formData.phone1.trim()) {
            errors.push('Numărul de telefon 1 este obligatoriu');
        } else if (!validateRomanianPhone(formData.phone1)) {
            errors.push('Numărul de telefon 1 nu este valid (folosiți formatul 07XXXXXXXX)');
        }

        if (formData.phone2.trim() && !validateRomanianPhone(formData.phone2)) {
            errors.push('Numărul de telefon 2 nu este valid (folosiți formatul 07XXXXXXXX)');
        }

        if (!formData.lastName.trim()) {
            errors.push('Numele este obligatoriu');
        }

        if (!formData.firstName.trim()) {
            errors.push('Prenumele este obligatoriu');
        }

        // if (!formData.cnp.trim()) {
        //     errors.push('CNP-ul este obligatoriu');
        // } else if (!validateCNP(formData.cnp)) {
        //     errors.push('CNP-ul nu este valid');
        // }
        if (!validateCNP(formData.cnp) && formData.cnp.trim()) {
            errors.push('CNP-ul nu este valid');
        }

        // if (!formData.idSeries.trim()) {
        //     errors.push('Seria actului de identitate este obligatorie');
        // }
        //
        // if (!formData.address.trim()) {
        //     errors.push('Adresa este obligatorie');
        // }
        //
        // if (selectedFurnitureTypes.length === 0) {
        //     errors.push('Tipul de mobilă este obligatoriu');
        // }

        return errors;
    };

    const hasChanges = () => {
        if (!isEditMode || !initialData) return false;

        // Verifică modificări în formData
        const currentFormData = {
            date: formData.date,
            contractNumber: formData.contractNumber,
            clientType: formData.clientType === 'Persoană juridică' ? 'PERS_JURIDICA' : 'PERS_FIZICA',
            email: formData.email,
            phone1: formData.phone1,
            phone2: formData.phone2,
            firstName: formData.firstName,
            lastName: formData.lastName,
            cnp: formData.cnp,
            idSeries: formData.idSeries,
            address: formData.address,
            furnitureType: formatFurnitureTypes(selectedFurnitureTypes),
            details: formData.details,
            advance: formData.advance,
            total: formData.total,
            advancePaymentMethod: mapPaymentMethodToEnum(formData.advancePaymentMethod),
            restPaymentMethod: mapPaymentMethodToEnum(formData.restPaymentMethod),
            companyName: formData.companyName,
            cui: formData.cui,
            companyAddress: formData.companyAddress
        };

        const originalFormData = {
            date: initialData.date || new Date().toISOString().split('T')[0],
            contractNumber: initialData.contractNumber || '02',
            clientType: initialData.clientType || 'PERS_FIZICA',
            email: initialData.email || '',
            phone1: initialData.phone1 || '',
            phone2: initialData.phone2 || '',
            firstName: initialData.firstName || '',
            lastName: initialData.lastName || '',
            cnp: initialData.cnp || '',
            idSeries: initialData.idSeries || '',
            address: initialData.address || '',
            furnitureType: initialData.furnitureType || '',
            details: initialData.details || '',
            advance: initialData.advance || (initialData.initialPayment ? initialData.initialPayment.toString() : ''),
            total: initialData.total || (initialData.totalAmount ? initialData.totalAmount.toString() : ''),
            advancePaymentMethod: initialData.initialPaymentType || 'CASH',
            restPaymentMethod: initialData.remainingPaymentType || 'CARD',
            companyName: initialData.companyName || '',
            cui: initialData.cui || '',
            companyAddress: initialData.companyAddress || ''
        };

        for (const key in currentFormData) {
            if (currentFormData[key] !== originalFormData[key]) {
                return true;
            }
        }

        if (measurements.length !== initialMeasurements.length) return true;
        for (let i = 0; i < measurements.length; i++) {
            const current = measurements[i];
            const original = initialMeasurements[i];
            if (!original ||
                current.date !== original.date ||
                current.startTime !== original.startTime ||
                current.endTime !== original.endTime ||
                current.description !== original.description) {
                return true;
            }
        }

        if (installations.length !== initialInstallations.length) return true;
        for (let i = 0; i < installations.length; i++) {
            const current = installations[i];
            const original = initialInstallations[i];
            if (!original ||
                current.date !== original.date ||
                current.startTime !== original.startTime ||
                current.endTime !== original.endTime ||
                current.description !== original.description) {
                return true;
            }
        }

        if (selectedImages.length !== initialImages.length) return true;
        if (newImages.length > 0 || deletedImageIds.length > 0) return true;

        if (customerReview) {
            if (!initialData.review && customerReview) return true;
            if (initialData.review &&
                (customerReview.reviewType !== initialData.review.reviewType ||
                    customerReview.description !== initialData.review.description)) {
                return true;
            }
        }

        if (isRemainingPaymentPaid !== (initialData.isRemainingPaymentPaid || false)) {
            return true;
        }

        return false;
    };

    const handleCloseWithConfirmation = () => {
        if (isEditMode && hasChanges()) {
            setShowSaveConfirmModal(true);
            return;
        }
        onClose();
    };

    // Check if client is a company (juridical person)
    const isCompany = formData.clientType === 'Persoană juridică';

    const mapPaymentMethodToEnum = (paymentMethod) => {
        switch (paymentMethod) {
            case 'Cash':
                return 'CASH';
            case 'Card':
                return 'CARD';
            default:
                return 'CASH';
        }
    };

    const mapEnumToPaymentMethod = (enumValue) => {
        switch (enumValue) {
            case 'CASH':
                return 'Cash';
            case 'CARD':
                return 'Card';
            default:
                return 'Cash';
        }
    };

    // Parse furniture types from string to array
    const parseFurnitureTypes = (furnitureTypeString) => {
        if (!furnitureTypeString) return [];
        return furnitureTypeString.split(', ').filter(type => type.trim());
    };

    // Convert furniture types array to string
    const formatFurnitureTypes = (furnitureTypesArray) => {
        return furnitureTypesArray.join(', ');
    };

    // Initialize form with existing data if provided (editing mode)
    useEffect(() => {
        if (initialData) {
            console.log('Initializing form with data:', initialData);
            console.log('Payment types from backend:', {
                initialPaymentType: initialData.initialPaymentType,
                remainingPaymentType: initialData.remainingPaymentType,
                isRemainingPaymentPaid: initialData.isRemainingPaymentPaid
            });

            // Create a new form data object with all fields
            const updatedFormData = {
                ...formData,  // Start with default values

                // First set basic fields
                date: initialData.date || new Date().toISOString().split('T')[0],
                contractNumber: initialData.contractNumber || '02',
                email: initialData.email || '',
                phone1: initialData.phone1 || '',
                phone2: initialData.phone2 || '',
                firstName: initialData.firstName || '',
                lastName: initialData.lastName || '',
                cnp: initialData.cnp || '',

                // Handle specific field mappings
                idSeries: initialData.idSeries || '',
                address: initialData.address || '',
                furnitureType: initialData.furnitureType || '',
                details: initialData.details || '',
                advance: initialData.advance || (initialData.initialPayment ? initialData.initialPayment.toString() : ''),
                total: initialData.total || (initialData.totalAmount ? initialData.totalAmount.toString() : ''),
                rest: initialData.rest || (initialData.totalAmount && initialData.initialPayment
                    ? (initialData.totalAmount - initialData.initialPayment).toString()
                    : ''),

                // Map payment methods from backend to frontend
                advancePaymentMethod: mapEnumToPaymentMethod(initialData.initialPaymentType),
                restPaymentMethod: mapEnumToPaymentMethod(initialData.remainingPaymentType),

                // Handle contract mapping from API to UI format
                contract: mapActivityToContract(initialData.activity),

                // Always set company fields regardless of client type
                companyName: initialData.companyName || '',
                cui: initialData.cui || '',
                companyAddress: initialData.companyAddress || '',

                // Properly map clientType
                clientType: initialData.clientType === 'PERS_JURIDICA'
                    ? 'Persoană juridică'
                    : 'Persoană fizică'
            };

            // Debug the mapped client type
            console.log('Mapped client type for UI:', updatedFormData.clientType);
            console.log('Company fields:', {
                companyName: updatedFormData.companyName,
                cui: updatedFormData.cui,
                companyAddress: updatedFormData.companyAddress
            });

            setFormData(updatedFormData);

            // Parse and set furniture types
            const furnitureTypes = parseFurnitureTypes(initialData.furnitureType);
            setSelectedFurnitureTypes(furnitureTypes);

            if (initialData.id) {
                setCustomerId(initialData.id);
                setIsEditMode(true);

                setIsRemainingPaymentPaid(initialData.isRemainingPaymentPaid || false);
                // If we have a customer ID and this is the first time opening the modal,
                // fetch the customer's review but don't fetch attachments yet
                if (!customerReview) {
                    fetchCustomerReview(initialData.id);
                }
            }

            if (initialData.measurements) {
                setMeasurements(initialData.measurements);
                setInitialMeasurements([...initialData.measurements]);
            }

            if (initialData.installations) {
                setInstallations(initialData.installations);
                setInitialInstallations([...initialData.installations]);
            }

            // If images are provided in initialData, use them without fetching
            if (initialData.images && initialData.images.length > 0) {
                setSelectedImages(initialData.images);
                setInitialImages([...initialData.images]);
                setAttachmentsLoaded(true);
            }

        } else {
            // Reset form for new client
            setFormData({
                date: new Date().toISOString().split('T')[0],
                contractNumber: '02',
                clientType: 'Persoană fizică',
                contract: '',
                email: '',
                phone1: '',
                phone2: '',
                firstName: '',
                lastName: '',
                cnp: '',
                idSeries: '',
                furnitureType: '',
                address: '',
                details: '',
                advance: '',
                advancePaymentMethod: 'Cash',
                rest: '',
                restPaymentMethod: 'Card',
                total: '',
                companyName: '',
                cui: '',
                companyAddress: ''
            });
            setCustomerId(null);
            setIsEditMode(false);
            setMeasurements([]);
            setInstallations([]);
            setInitialMeasurements([]);
            setInitialInstallations([]);
            setSelectedImages([]);
            setInitialImages([]);
            setNewImages([]);
            setDeletedImageIds([]);
            setCustomerReview(null);
            setAttachmentsLoaded(false);
            setIsRemainingPaymentPaid(false);
            setSelectedFurnitureTypes([]);

            setContractNumber(null);
            setHasExistingContract(false);
        }
    }, [initialData, isOpen]);

    useEffect(() => {
        if (customerId && isEditMode) {
            checkExistingContract();
        }
    }, [customerId, isEditMode]);

    const checkExistingContract = async () => {
        if (!customerId) return;

        try {
            setIsCheckingContract(true);
            const token = localStorage.getItem('token');

            // Check if customer has any contracts
            const response = await axios.get(`${BASE_URL}/contract/customer/${customerId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // If we get contracts back, customer has existing contract(s)
            if (response.data && response.data.contractNumber) {
                setHasExistingContract(true);
                setContractNumber(response.data.contractNumber);
            } else {
                setHasExistingContract(false);
                setContractNumber(null);
            }

        } catch (error) {
            // If we get 404 or error, assume no contract exists
            console.log('No existing contract found for customer:', customerId);
            setHasExistingContract(false);
            setContractNumber(null);
        } finally {
            setIsCheckingContract(false);
        }
    };

    // Helper function to map Activity enum to Contract UI value
    const mapActivityToContract = (activity) => {
        switch (activity) {
            case 'CONTRACT':
                return 'Contract';
            case 'IN_OFERTARE':
                return 'In ofertare';
            case 'FARA_CONTRACT':
                return 'Fara contract';
            default:
                return '';
        }
    };

    // Function to fetch attachments only when needed (lazy loading)
    const fetchAttachmentsIfNeeded = async () => {
        if (!customerId || attachmentsLoaded) return;

        try {
            // Fetch photo attachments - use uppercase to match backend enum
            const photoAttachments = await AttachmentService.getAttachments(customerId, 'PHOTO');

            // Fetch project (drawing) attachments
            const projectAttachments = await AttachmentService.getAttachments(customerId, 'PROJECT');

            console.log("Fetched attachments for customer:", customerId, {
                photoAttachments,
                projectAttachments
            });

            // Process and set the fetched images
            const processedPhotoImages = photoAttachments.map(attachment => ({
                id: attachment.id || Date.now() + Math.random(),
                url: attachment.url,
                attachId: attachment.attachId || attachment.id,
                attachType: 'PHOTO'
            }));

            const processedProjectImages = projectAttachments.map(attachment => ({
                id: attachment.id || Date.now() + Math.random(),
                url: attachment.url,
                attachId: attachment.attachId || attachment.id,
                isDrawing: true,
                attachType: 'PROJECT'
            }));

            // Combine all attachments for the selected images state
            const allImages = [...processedPhotoImages, ...processedProjectImages];
            setSelectedImages(allImages);
            setInitialImages([...allImages]);
            setAttachmentsLoaded(true);
        } catch (error) {
            console.error('Error fetching attachments for customer:', customerId, error);
        }
    };

    const fetchCustomerReview = async (id) => {
        if (!id) return;

        try {
            setIsLoadingReview(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${USER_ENDPOINTS.GET_CUSTOMER_REVIEW}/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Extract the first review from the array
            const reviewData = response.data && response.data.length > 0 ? response.data[0] : null;
            console.log("Processed review data:", reviewData);

            // Set the extracted review object to state
            setCustomerReview(reviewData);
        } catch (error) {
            console.error('Error fetching customer review:', error);
        } finally {
            setIsLoadingReview(false);
        }
    };

    const handleChange = (e) => {
        const {name, value} = e.target;

        // Special handling for client type changes
        if (name === 'clientType') {
            console.log('Client type changed to:', value);

            // Keep the rest of the form state unchanged,
            // just update the clientType field
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));

            // No need to clear company fields when switching to 'Persoană fizică'
            // They will be retained in the state but not displayed
            // When the form is submitted, the fields will be set to null in the API payload
            // if the client type is 'Persoană fizică'
        }
        // Handle regular field changes
        else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));

            // Calculate total or rest when advance changes
            if (name === 'advance' || name === 'total') {
                const advance = name === 'advance' ? parseFloat(value) || 0 : parseFloat(formData.advance) || 0;
                const total = name === 'total' ? parseFloat(value) || 0 : parseFloat(formData.total) || 0;

                if (total >= advance) {
                    setFormData(prevState => ({
                        ...prevState,
                        rest: (total - advance).toString()
                    }));
                }
            }
        }
    };

    // Handle furniture type selection
    const handleFurnitureTypeToggle = (type) => {
        setSelectedFurnitureTypes(prev => {
            if (prev.includes(type)) {
                return prev.filter(t => t !== type);
            } else {
                return [...prev, type];
            }
        });
    };
    const handleClientTypeSelect = (type) => {
        setFormData(prevState => ({
            ...prevState,
            clientType: type
        }));
        setShowClientTypeDropdown(false);
    };
    const performSave = async () => {
        setIsSubmitting(true);
        setError('');

        try {
            // Validate form
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                setError(validationErrors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            // NEW VALIDATION: Check if there's at least one measurement or installation
            if (measurements.length === 0 && installations.length === 0) {
                setError('Este obligatoriu să adăugați cel puțin o măsurătoare sau un montaj pentru client.');
                setIsSubmitting(false);
                return;
            }

            const newMeasurements = measurements.filter(m =>
                !m.id || !initialMeasurements.some(im => im.id === m.id)
            );
            const newInstallations = installations.filter(i =>
                !i.id || !initialInstallations.some(ii => ii.id === i.id)
            );

            const pastNewMeasurements = newMeasurements.filter(m => isPastDate(m.date));
            const pastNewInstallations = newInstallations.filter(i => isPastDate(i.date));

            if (pastNewMeasurements.length > 0 || pastNewInstallations.length > 0) {
                setError('Nu poți programa măsurători sau montaje noi în trecut. Te rugăm să selectezi doar date din viitor pentru evenimentele noi.');
                setIsSubmitting(false);
                return;
            }

            // Map clientType to API format
            const clientTypeMapping = {
                'Persoană fizică': 'PERS_FIZICA',
                'Persoană juridică': 'PERS_JURIDICA'
            };

            // Map contract type to API format (auto-calculated by frontend)
            const contractMapping = {
                'Contract': 'CONTRACT',
                'In ofertare': 'IN_OFERTARE',
                'Fara contract': 'FARA_CONTRACT',
                '': 'IN_OFERTARE' // Default if not selected
            };

            // Prepare client data payload
            const clientData = {
                id: customerId, // This will be null for new customers
                clientType: clientTypeMapping[formData.clientType] || 'PERS_FIZICA',
                activity: contractMapping[formData.contract] || 'IN_OFERTARE', // Use auto-calculated contract status
                email: formData.email,
                phone1: formData.phone1,
                phone2: formData.phone2,
                lastName: formData.lastName,
                firstName: formData.firstName,
                cnp: formData.cnp,
                serialNumber: formData.idSeries,
                furnitureType: formatFurnitureTypes(selectedFurnitureTypes), // Convert array to string
                homeAddress: formData.address,
                installationAddress: formData.address, // Using same address for both
                details: formData.details,
                totalAmount: parseFloat(formData.total) || 0,
                initialPayment: parseFloat(formData.advance) || 0,
                initialPaymentType: mapPaymentMethodToEnum(formData.advancePaymentMethod),
                isRemainingPaymentPaid: isRemainingPaymentPaid,
                remainingPaymentType: mapPaymentMethodToEnum(formData.restPaymentMethod)
            };

            // Add company fields if client type is 'Persoană juridică'
            if (formData.clientType === 'Persoană juridică') {
                clientData.companyName = formData.companyName;
                clientData.cui = formData.cui;
                clientData.companyAddress = formData.companyAddress;
            } else {
                // Set company fields to null if client type is 'Persoană fizică'
                clientData.companyName = null;
                clientData.cui = null;
                clientData.companyAddress = null;
            }

            // Get token from localStorage
            const token = localStorage.getItem('token');

            let customerResponse;
            // If we have a customerId, update the existing customer
            if (customerId) {
                // Use the direct endpoint with customer ID for PUT request
                customerResponse = await axios.put(`${USER_ENDPOINTS.GET_CUSTOMER}/${customerId}`, clientData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                // Create new customer
                customerResponse = await axios.post(USER_ENDPOINTS.CREATE_CUSTOMER, clientData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // Get the customer ID from the response or use existing one
            const customerIdToUse = customerResponse.data.id || customerId;

            // Update our state with the new ID
            setCustomerId(customerIdToUse);

            // Process measurements and installations if present
            const newMeasurementEvents = [];
            const newInstallationEvents = [];
            const allUpdateEvents = [];
            const allDeleteEvents = [];

            // Process measurements - identify new, updated, and deleted
            measurements.forEach(measurement => {
                const eventData = {
                    scheduledDate: measurement.date,
                    startTime: measurement.startTime + ':00',
                    endTime: measurement.endTime + ':00',
                    description: measurement.description || 'Măsurătoare',
                    eventType: 'MASURATORI'
                };

                if (measurement.id && initialMeasurements.some(m => m.id === measurement.id)) {
                    // This is an existing measurement, update it
                    allUpdateEvents.push({
                        ...eventData,
                        id: measurement.id
                    });
                } else {
                    // This is a new measurement
                    newMeasurementEvents.push(eventData);
                }
            });

            // Identify deleted measurements
            initialMeasurements.forEach(initialMeasurement => {
                if (!measurements.some(m => m.id === initialMeasurement.id)) {
                    allDeleteEvents.push(initialMeasurement.id);
                }
            });

            // Process installations - identify new, updated, and deleted
            installations.forEach(installation => {
                const eventData = {
                    scheduledDate: installation.date,
                    startTime: installation.startTime + ':00',
                    endTime: installation.endTime + ':00',
                    description: installation.description || 'Montaj',
                    eventType: 'MONTAJ'
                };

                if (installation.id && initialInstallations.some(i => i.id === installation.id)) {
                    // This is an existing installation, update it
                    allUpdateEvents.push({
                        ...eventData,
                        id: installation.id
                    });
                } else {
                    // This is a new installation
                    newInstallationEvents.push(eventData);
                }
            });

            // Identify deleted installations
            initialInstallations.forEach(initialInstallation => {
                if (!installations.some(i => i.id === initialInstallation.id)) {
                    allDeleteEvents.push(initialInstallation.id);
                }
            });

            // 1. Create new measurement events
            if (newMeasurementEvents.length > 0) {
                const measurementsPayload = {
                    customerId: customerIdToUse,
                    eventType: "MASURATORI",
                    events: newMeasurementEvents
                };

                await axios.post(USER_ENDPOINTS.CREATE_CUSTOMER_EVENT, measurementsPayload, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // 2. Create new installation events
            if (newInstallationEvents.length > 0) {
                const installationsPayload = {
                    customerId: customerIdToUse,
                    eventType: "MONTAJ",
                    events: newInstallationEvents
                };

                await axios.post(USER_ENDPOINTS.CREATE_CUSTOMER_EVENT, installationsPayload, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // 3. Update existing events
            for (const event of allUpdateEvents) {
                await axios.put(USER_ENDPOINTS.UPDATE_EVENT, event, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // 4. Delete removed events
            for (const eventId of allDeleteEvents) {
                await axios.delete(`${USER_ENDPOINTS.DELETE_EVENT}${eventId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            }

            // Process attachments:
            // 1. Delete attachments that were removed
            for (const attachId of deletedImageIds) {
                try {
                    await AttachmentService.deleteAttachment(attachId);
                } catch (err) {
                    console.error(`Error deleting attachment ${attachId}:`, err);
                    // Continue with other operations even if deletion fails
                }
            }

            // 2. Upload new attachments
            for (const image of newImages) {
                try {
                    if (image.isDrawing && image.url.startsWith('data:')) {
                        // For drawings from canvas
                        const imageBlob = AttachmentService.dataURLtoBlob(image.url);
                        await AttachmentService.createAttachment({
                            customerId: customerIdToUse,
                            files: imageBlob,
                            attachType: 'project' // Will be converted to PROJECT in the service
                        });
                    } else if (image.file) {
                        // For regular images (photos)
                        await AttachmentService.createAttachment({
                            customerId: customerIdToUse,
                            files: image.file,
                            attachType: 'photo' // Will be converted to PHOTO in the service
                        });
                    }
                } catch (err) {
                    console.error('Error uploading attachment:', err);
                    // Continue with other attachments even if one fails
                }
            }

            // 5. Save or update the customer review
            if (customerReview) {
                try {
                    const reviewData = {
                        customerId: customerIdToUse,
                        reviewType: customerReview.reviewType,
                        description: customerReview.description
                    };

                    if (customerReview.id) {
                        // Update existing review
                        await axios.put(`${USER_ENDPOINTS.CREATE_UPDATE_REVIEW}/${customerReview.id}`, reviewData, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                    } else {
                        // Create new review
                        await axios.post(USER_ENDPOINTS.CREATE_UPDATE_REVIEW, reviewData, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                    }
                } catch (err) {
                    console.error('Error saving review:', err);
                    // Continue even if review saving fails
                }
            }

            // Clear attachment cache for this customer to ensure fresh data on next fetch
            AttachmentService.clearCache(customerIdToUse);

            // Call onSave with the combined data
            if (onSave) {
                onSave({
                    ...formData,
                    id: customerIdToUse,
                    measurements,
                    installations,
                    images: selectedImages,
                    review: customerReview
                });
            }

            onClose();
        } catch (err) {
            console.error('Error saving client:', err);
            setError(err.response?.data?.message || 'A apărut o eroare la salvarea clientului. Vă rugăm să încercați din nou.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveConfirm = () => {
        setShowSaveConfirmModal(false);
        onClose(); // Close the modal instead of saving
    };

    const handleSaveCancel = () => {
        setShowSaveConfirmModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await performSave();
    };

    const handleMeasurementsSave = (items) => {
        setMeasurements(items);
        setShowMeasurementsModal(false);
    };

    const handleInstallationsSave = (items) => {
        setInstallations(items);
        setShowInstallationsModal(false);
    };

    const handleImagesSave = (result) => {
        // Update the selected images state with the new selection
        setSelectedImages(result.selectedImages);

        // Track new images that need to be uploaded
        const newUploadImages = result.newImages || [];
        if (newUploadImages.length > 0) {
            setNewImages([...newImages, ...newUploadImages]);
        }

        // Track image IDs that need to be deleted
        const imageIdsToDelete = result.deletedImageIds || [];
        if (imageIdsToDelete.length > 0) {
            setDeletedImageIds([...deletedImageIds, ...imageIdsToDelete]);
        }

        setShowGalleryModal(false);
    };

    const handleDrawingSave = (drawingImage) => {
        // Add drawing to selected images
        setSelectedImages([...selectedImages, drawingImage]);
        // Add to new images to be uploaded on save
        setNewImages([...newImages, drawingImage]);
        setShowDrawingCanvas(false);
    };

    const handleReviewSave = (reviewData) => {
        // Just update the local state without sending to server yet
        setCustomerReview(reviewData);
        setShowReviewModal(false);
    };

    const openDrawingCanvas = () => {
        console.log("Deschid canvas-ul de desen");
        setShowDrawingCanvas(true);
    };

    const handleIdScannerSave = (extractedData) => {
        // Actualizăm formData cu datele extrase din buletin
        setFormData(prevState => ({
            ...prevState,
            firstName: extractedData.firstName || prevState.firstName,
            lastName: extractedData.lastName || prevState.lastName,
            cnp: extractedData.cnp || prevState.cnp,
            idSeries: extractedData.idSeries || prevState.idSeries,
            address: extractedData.address || prevState.address
        }));
        setShowIdScanner(false);
    };

    // Open gallery modal and fetch attachments if needed
    const openGalleryModal = async () => {
        // Load attachments if they haven't been loaded yet
        if (!attachmentsLoaded && customerId) {
            await fetchAttachmentsIfNeeded();
        }
        setShowGalleryModal(true);
    };

    // Open review modal
    const openReviewModal = () => {
        setShowReviewModal(true);
    };

    // Get icon color based on review type
    const getReviewIconColor = (isWarning = true) => {
        if (isLoadingReview) return '#aaa'; // Gray while loading

        if (!customerReview) return '#aaa'; // Gray if no review

        if (isWarning) {
            // For warning/exclamation icon
            return customerReview.reviewType === 'NEGATIVE' ? '#e74c3c' : '#2ecc71';
        } else {
            // For chat icon - always active color when a review exists
            return '#3498db';
        }
    };

    const getPaymentSectionColor = (section) => {
        const hasAdvance = formData.advance && parseFloat(formData.advance) > 0;
        const hasRest = formData.rest && parseFloat(formData.rest) > 0;

        // Dacă nu este pus avans -> toate cu gri
        if (!hasAdvance) {
            return '#6c757d'; // gri
        }

        // Dacă s-a pus doar avansul -> avansul verde, rest de plată și total gri
        if (hasAdvance && !hasRest) {
            return section === 'advance' ? '#4caf50' : '#6c757d'; // verde pentru avans, gri pentru rest
        }

        // Dacă se pune și restul de plată, dar nu s-a apăsat achitat -> avansul verde, rest de plată și total roșii
        if (hasAdvance && hasRest && !isRemainingPaymentPaid) {
            return section === 'advance' ? '#4caf50' : '#dc3545'; // verde pentru avans, roșu pentru rest și total
        }

        // Dacă se apasă pe achitat -> toate verzi
        if (hasAdvance && isRemainingPaymentPaid) {
            return '#4caf50'; // verde pentru toate
        }

        return '#6c757d'; // fallback gri
    };

    const openContractModal = () => {
        if (!customerId && !isEditMode) {
            setError('Te rugăm să salvezi mai întâi clientul pentru a genera contractul.');
            return;
        }
        setShowContractModal(true);
    };

    if (!isOpen) return null;

    const handleRemainingPaymentToggle = () => {
        if (isRemainingPaymentPaid) {
            return;
        }

        setShowPaymentConfirmModal(true);
    };

    const handlePaymentConfirm = () => {
        setIsRemainingPaymentPaid(true);
        setShowPaymentConfirmModal(false);
    };

    const handlePaymentCancel = () => {
        setShowPaymentConfirmModal(false);
    };

    return (
        <div className="client-modal-overlay">
            <div className="client-modal-content">
                <div className="client-modal-header">
                    <div className="date-contract">
                        <span>{formData.date}</span>
                        <span>
            {isCheckingContract
                ? 'Se verifică contractul...'
                : contractNumber
                    ? `Nr. contract: ${contractNumber}`
                    : 'Contractul nu a fost generat'
            }
        </span>
                    </div>
                    <div className="header-icons">
                        <BsPencilSquare
                            className="header-icon"
                            onClick={openDrawingCanvas}
                            style={{cursor: 'pointer'}}
                        />
                        <BsCardImage
                            className="header-icon"
                            onClick={openGalleryModal}
                            style={{cursor: 'pointer'}}
                        />
                        <BsCameraFill
                            className="header-icon"
                            onClick={() => setShowIdScanner(true)}
                            style={{cursor: 'pointer'}}
                        />
                        <BsX
                            className="header-icon"
                            onClick={handleCloseWithConfirmation} // Changed from onClose
                            style={{cursor: 'pointer'}}
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="client-form-row">
                        <div className="client-form-field">
                            <label>Tip client</label>
                            <div className="client-type-dropdown" style={{position: 'relative', width: '100%'}}>
                                <div
                                    className="client-type-display"
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '0 4px 4px 0',
                                        padding: '8px 10px',
                                        minHeight: '38px',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    onClick={() => setShowClientTypeDropdown(!showClientTypeDropdown)}
                                >
            <span style={{
                backgroundColor: '#26c9bc',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '12px'
            }}>
                {formData.clientType}
            </span>
                                </div>
                                {showClientTypeDropdown && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            right: '0',
                                            backgroundColor: 'white',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            zIndex: 1000
                                        }}
                                    >
                                        {CLIENT_TYPES.map(type => (
                                            <div
                                                key={type}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: formData.clientType === type ? '#f0f0f0' : 'transparent'
                                                }}
                                                onClick={() => handleClientTypeSelect(type)}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = formData.clientType === type ? '#f0f0f0' : 'transparent'}
                                            >
                                                {type}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="client-form-field">
                            <label>Activitate</label>
                            <div style={{
                                flex: 1,
                                padding: '8px 10px',
                                border: '1px solid #ddd',
                                borderRadius: '0 4px 4px 0',
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '14px',
                                color: '#495057'
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: getContractStatusColor(formData.contract),
                                    color: 'white'
                                }}>
                                    {formData.contract || 'In ofertare'}
                                </span>
                                <span style={{marginLeft: '8px', fontSize: '11px', color: '#6c757d'}}>
                                    (calculat automat)
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field">
                            <label>Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="nume@domeniu.com"
                            />
                        </div>
                        <div className="client-form-field">
                            <label>
                                <a href={`tel:${formData.phone1}`}>
                                    Nr. Tel 1 *
                                </a>
                            </label>
                            <input
                                type="tel"
                                name="phone1"
                                value={formData.phone1}
                                onChange={handleChange}
                                placeholder="0740123456"
                                required
                            />
                        </div>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field">
                            <label>Recenzii</label>
                            <div className="reviews-container">
                                <BsExclamationTriangleFill
                                    className="review-icon"
                                    style={{
                                        cursor: 'pointer',
                                        color: getReviewIconColor(true)
                                    }}
                                    title={customerReview ?
                                        (customerReview.reviewType === 'NEGATIVE' ? 'Recenzie negativă' : 'Recenzie pozitivă')
                                        : 'Fără recenzie'}
                                />
                                <BsChatFill
                                    className="review-icon"
                                    style={{
                                        cursor: 'pointer',
                                        color: getReviewIconColor(false)
                                    }}
                                    onClick={openReviewModal}
                                    title="Adaugă/Editează recenzie"
                                />
                            </div>
                        </div>
                        <div className="client-form-field">
                            <label>
                                <a href={`tel:${formData.phone2}`}>
                                    Nr. Tel 2
                                </a>
                            </label>
                            <input
                                type="tel"
                                name="phone2"
                                value={formData.phone2}
                                onChange={handleChange}
                                placeholder="0740123456"
                            />
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button
                            type="button"
                            className="measure-button"
                            onClick={() => setShowMeasurementsModal(true)}
                        >
                            Măsurători {measurements.length > 0 && `(${measurements.length})`}
                        </button>
                        <button
                            type="button"
                            className="install-button"
                            onClick={() => setShowInstallationsModal(true)}
                        >
                            Montaje {installations.length > 0 && `(${installations.length})`}
                            {!installations.length && <span className="alert-badge">!</span>}
                        </button>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field">
                            <label>Nume *</label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="client-form-field">
                            <label>Prenume *</label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field">
                            <label>CNP</label>
                            <input
                                type="text"
                                name="cnp"
                                value={formData.cnp}
                                onChange={handleChange}
                                maxLength="13"
                            />
                        </div>
                        <div className="client-form-field">
                            <label>Serie</label>
                            <input
                                type="text"
                                name="idSeries"
                                value={formData.idSeries}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {isCompany && (
                        // Company fields - appear when client type is "Persoană juridică"
                        <>
                            <div className="client-form-row">
                                <div className="client-form-field">
                                    <label>Nume Companie</label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder="Numele companiei"
                                    />
                                </div>
                                <div className="client-form-field">
                                    <label>CUI</label>
                                    <input
                                        type="text"
                                        name="cui"
                                        value={formData.cui}
                                        onChange={handleChange}
                                        placeholder="Cod Unic de Înregistrare"
                                    />
                                </div>
                            </div>
                            <div className="client-form-row">
                                <div className="client-form-field full-width">
                                    <label>Adresa Companie</label>
                                    <input
                                        type="text"
                                        name="companyAddress"
                                        value={formData.companyAddress}
                                        onChange={handleChange}
                                        placeholder="Adresa sediului companiei"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="client-form-row">
                        <div className="client-form-field full-width">
                            <label>Tip mobilă</label>
                            <div className="furniture-multiselect" style={{position: 'relative', width: '100%'}}>
                                <div
                                    className="furniture-display"
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '0 4px 4px 0',
                                        padding: '8px 10px',
                                        minHeight: '38px',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '4px'
                                    }}
                                    onClick={() => setShowFurnitureDropdown(!showFurnitureDropdown)}
                                >
                                    {selectedFurnitureTypes.length === 0 ? (
                                        <span style={{color: '#999'}}>Selectați tipurile de mobilă</span>
                                    ) : (
                                        selectedFurnitureTypes.map(type => (
                                            <span
                                                key={type}
                                                style={{
                                                    backgroundColor: '#26c9bc',
                                                    color: 'white',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                {type}
                                            </span>
                                        ))
                                    )}
                                </div>
                                {showFurnitureDropdown && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            right: '0',
                                            backgroundColor: 'white',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            zIndex: 1000
                                        }}
                                    >
                                        {FURNITURE_TYPES.map(type => (
                                            <div
                                                key={type}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedFurnitureTypes.includes(type) ? '#f0f0f0' : 'transparent'
                                                }}
                                                onClick={() => handleFurnitureTypeToggle(type)}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = selectedFurnitureTypes.includes(type) ? '#f0f0f0' : 'transparent'}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFurnitureTypes.includes(type)}
                                                    onChange={() => {
                                                    }}
                                                    style={{marginRight: '8px'}}
                                                />
                                                {type}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field full-width">
                            <label>Adresa</label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Str. Example, nr. 123, ap. 45, bloc A1, Oraș, Județ"
                            />
                        </div>
                    </div>

                    <div className="client-form-row">
                        <div className="client-form-field full-width">
                            <label>Detalii</label>
                            <input
                                type="text"
                                name="details"
                                value={formData.details}
                                onChange={handleChange}
                                placeholder="Detalii suplimentare despre comandă"
                            />
                        </div>
                    </div>

                    <div className="payment-section">
                        <div className="payment-column">
                            <div className="payment-header"
                                 style={{backgroundColor: getPaymentSectionColor('advance')}}>AVANS
                            </div>
                            <div className="payment-amount">
                                <input
                                    type="text"
                                    name="advance"
                                    value={formData.advance}
                                    onChange={handleChange}
                                    placeholder="0"
                                    disabled={isAdvanceDisabled()}
                                    style={{
                                        backgroundColor: isAdvanceDisabled() ? '#f8f9fa' : 'white',
                                        cursor: isAdvanceDisabled() ? 'not-allowed' : 'text'
                                    }}
                                /> lei {getPaymentIcon(formData.advancePaymentMethod)}
                            </div>
                            <div className="payment-method">
                                <select
                                    name="advancePaymentMethod"
                                    value={formData.advancePaymentMethod}
                                    onChange={handleChange}
                                    disabled={isAdvanceDisabled()}
                                    style={{
                                        backgroundColor: isAdvanceDisabled() ? '#f8f9fa' : 'white',
                                        cursor: isAdvanceDisabled() ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <option>Cash</option>
                                    <option>Card</option>
                                </select>
                            </div>
                        </div>

                        <div className="payment-column">
                            <div className="payment-header"
                                 style={{backgroundColor: getPaymentSectionColor('rest')}}>REST - PLATĂ
                            </div>
                            <div className="payment-amount">
                                <input
                                    type="text"
                                    name="rest"
                                    value={formData.rest}
                                    onChange={handleChange}
                                    placeholder="0"
                                    disabled={true}
                                    style={{backgroundColor: '#f8f9fa'}}
                                /> lei {getPaymentIcon(formData.restPaymentMethod)}
                            </div>
                            <div className="payment-method">
                                <select
                                    name="restPaymentMethod"
                                    value={formData.restPaymentMethod}
                                    onChange={handleChange}
                                    disabled={isRemainingPaymentPaid}
                                    style={{
                                        backgroundColor: isRemainingPaymentPaid ? '#f8f9fa' : 'white',
                                        cursor: isRemainingPaymentPaid ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <option>Card</option>
                                    <option>Cash</option>
                                </select>
                            </div>
                        </div>

                        <div className="payment-column">
                            <div className="payment-header"
                                 style={{backgroundColor: getPaymentSectionColor('total')}}>TOTAL
                            </div>
                            <div className="payment-amount">
                                <input
                                    type="text"
                                    name="total"
                                    value={formData.total}
                                    onChange={handleChange}
                                    placeholder="0"
                                    disabled={isTotalDisabled()}
                                    style={{
                                        backgroundColor: isTotalDisabled() ? '#f8f9fa' : 'white',
                                        cursor: isTotalDisabled() ? 'not-allowed' : 'text'
                                    }}
                                /> lei
                            </div>
                            <div className="payment-status-container">
                                <div
                                    className={`payment-status ${isRemainingPaymentPaid ? 'paid' : 'unpaid'}`}
                                    onClick={handleRemainingPaymentToggle}
                                    style={{
                                        cursor: isRemainingPaymentPaid ? 'not-allowed' : 'pointer',
                                        opacity: isRemainingPaymentPaid ? 0.8 : 1,
                                        textAlign: 'center',
                                        padding: '10px',
                                        borderRadius: '0px',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {isRemainingPaymentPaid ? 'ACHITAT' : 'NEACHITAT'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message" style={{color: 'red', margin: '10px 0', whiteSpace: 'pre-line'}}>
                            {error}
                        </div>
                    )}

                    <div className="form-buttons">
                        <button type="button" className="cancel-button" onClick={handleCloseWithConfirmation}>
                            Anulează
                        </button>
                        <button type="button" className="generate-button" onClick={openContractModal}
                                disabled={isCheckingContract}>
                            {isCheckingContract
                                ? 'Se verifică...'
                                : hasExistingContract
                                    ? 'Vizualizare contract'
                                    : 'Generare contract'
                            }
                        </button>
                        <button
                            type="submit"
                            className="save-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Se salvează...' : isEditMode ? 'Salvează' : 'Salvează'}
                        </button>
                    </div>
                </form>

                {/* Close dropdown when clicking outside */}
                {showFurnitureDropdown && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999
                        }}
                        onClick={() => setShowFurnitureDropdown(false)}
                    />
                )}

                {/* Modalul de măsurători */}
                <DynamicModal
                    isOpen={showMeasurementsModal}
                    onClose={() => setShowMeasurementsModal(false)}
                    type="measurements"
                    initialItems={measurements}
                    onSave={handleMeasurementsSave}
                    preventPastDates={true}
                />

                {/* Modalul de montaje */}
                <DynamicModal
                    isOpen={showInstallationsModal}
                    onClose={() => setShowInstallationsModal(false)}
                    type="installations"
                    initialItems={installations}
                    onSave={handleInstallationsSave}
                    preventPastDates={true}
                />

                {/* Modalul galerie de imagini */}
                <GalleryModal
                    isOpen={showGalleryModal}
                    onClose={() => setShowGalleryModal(false)}
                    initialImages={selectedImages}
                    onSave={handleImagesSave}
                    customerId={customerId}
                />

                {/* Modalul canvas pentru desen */}
                {showDrawingCanvas && (
                    <DrawingCanvas
                        isOpen={showDrawingCanvas}
                        onClose={() => setShowDrawingCanvas(false)}
                        onSave={handleDrawingSave}
                        customerId={customerId}
                    />
                )}

                {/* Scanner-ul pentru buletine */}
                <IdCardScanner
                    isOpen={showIdScanner}
                    onClose={() => setShowIdScanner(false)}
                    onSave={handleIdScannerSave}
                />

                {/* Modal pentru recenzii */}
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => setShowReviewModal(false)}
                    customerId={customerId || initialData?.id}
                    existingReview={customerReview}
                    onSave={handleReviewSave}
                />
                <ContractModal
                    isOpen={showContractModal}
                    onClose={() => setShowContractModal(false)}
                    clientData={{
                        id: customerId || initialData?.id,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phone1: formData.phone1,
                        phone2: formData.phone2,
                        furnitureType: formatFurnitureTypes(selectedFurnitureTypes),
                        total: formData.total,
                        advance: formData.advance,
                        address: formData.address
                    }}
                />
                <ConfirmationModal
                    isOpen={showPaymentConfirmModal}
                    onConfirm={handlePaymentConfirm}
                    onCancel={handlePaymentCancel}
                    message="Confirmați că restul de plată a fost achitat?"
                />
                <ConfirmationModal
                    isOpen={showSaveConfirmModal}
                    onConfirm={handleSaveConfirm}
                    onCancel={handleSaveCancel}
                    message="Aveți modificări nesalvate. Sunteți sigur că doriți să închideți fără să salvați?"
                />
            </div>
        </div>
    );


}

export default AddClientModal;